// Moved from `src/hooks/query/useQueryRoles.ts` (now the delegate) with OT3
// slice 2 (ARCFE-955). Two severances, both as explicit inputs:
// - `translateRoles` transformed the DATA inside queryFn (the cached value is
//   translation-dependent), so it crosses as an injected transform — the
//   delegate supplies the real `useTranslationHelpers().translateRoles` and
//   the cached shape stays byte-identical for identical translations.
// - The transport-error toast crosses the `onFetchError` port (ARCFE-921); the
//   rethrow after it is frozen behaviour.
import { useQuery } from '@tanstack/react-query';

import { GET_ROLES } from '../../contracts/keys';
import type { GetRolesResponse } from '../../contracts/types/organization-teams.types';
import { getRoles } from '../api/organization';
import {
  getWorkspaceDataConfig,
  isTransportError,
  type WorkspaceHttpError,
} from '../config';
import { useQueryUser } from './useQueryUser';

export { GET_ROLES };

interface UseQueryRolesOptions {
  /** The i18n data transform the legacy hook applied inside queryFn. */
  translateRoles: (roles: GetRolesResponse['data']) => GetRolesResponse['data'];
  /** App-reaction port: fired where the legacy toast fired (ARCFE-921). */
  onFetchError?: (error: WorkspaceHttpError) => void;
}

export function useQueryRoles(
  teamId: string | undefined,
  { translateRoles, onFetchError }: UseQueryRolesOptions
) {
  const token = getWorkspaceDataConfig().useAuthToken();
  const { data: userData } = useQueryUser();

  return useQuery({
    queryKey: [GET_ROLES, { teamId }],
    // Gate on token: persisted `userData` rehydrates pre-auth, so `!!userData` alone fires roles and 401s (SCR-5556).
    enabled: !!token && !!userData,
    queryFn: async () => {
      try {
        const { data } = await getRoles(teamId);

        if (data && data.data) {
          return {
            ...data,
            data: translateRoles(data.data),
          };
        }

        return data;
      } catch (error) {
        if (isTransportError(error)) {
          onFetchError?.(error);
        }
        throw error;
      }
    },
  });
}
